import { Flex, Link, Spinner, Text, useToast, VStack } from '@chakra-ui/react';
import { IconButton } from '@gamma/ui/buttons';
import { Input, Switch } from '@gamma/ui/form-fields';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import _ from 'lodash';
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Controller, useForm, ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { URLS } from '../../../../shared/constants/constants';
import { ExporterType } from '../../../../shared/generated/graphql';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers';
import { useSetExporterFormState } from '../../hooks';
import {
  ExporterTLSInfoInput,
  LogscaleExporter,
  useSaveExporter,
} from '../../queries';
import { validateExporterUrl } from '../../../../shared/utils/validateExporterUrl';
import { EXPORTER_DEFAULTS } from '../../pages/IntegrationsConstants';
import { ExporterSslSwitch, OnChangeSwitchProps } from '../ExporterSslSwitch';

const { LOG_SCALE_DOCS } = URLS;

interface HumioFormProps {
  isUpdating: boolean;
  deleteLoading: boolean;
  onDeleteOpen: () => void;
  clearInProgress: () => void;
  activeExporter: LogscaleExporter | null;
  setIsUpdating: (isUpdating: boolean) => void;
  setIsSaveLoading: (isSaveLoading: boolean) => void;
  setIsSaveDisabled: (isSaveDisabled: boolean) => void;
  setIsFormUnchanged: (isFormUnchanged: boolean) => void;
  setIsCancelDisabled: (isCancelDisabled: boolean) => void;
  setFormRef: (formRef: RefObject<HTMLFormElement> | null) => void;
  refetchExporters: () => Promise<unknown>;
}

interface AlertExportData {
  name: string;
  enabled: boolean;
  tls: ExporterTLSInfoInput;
  url: string;
  token: string;
  index: string;
}

export const HumioForm = ({
  isUpdating,
  setFormRef,
  onDeleteOpen,
  deleteLoading,
  setIsUpdating,
  activeExporter,
  clearInProgress,
  setIsSaveLoading,
  setIsSaveDisabled,
  setIsFormUnchanged,
  setIsCancelDisabled,
  refetchExporters,
}: HumioFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<AlertExportData>({
    mode: 'onChange',
    defaultValues: {
      ...EXPORTER_DEFAULTS,
      url: '',
      token: '',
      index: '',
    },
  });

  const { enabled: enabledLabel, disabled: disabledLabel } =
    i18n.pages.integrations;

  const { forms, humioForm, toast } = i18n.pages.alertExport;

  const { humioInstructions, humioDocumentation } = humioForm;

  const { saveSuccessDesc, saveSuccessTitle, saveFailDesc, saveFailTitle } =
    toast;

  const navigate = useNavigate();
  const showToastMessage = useToast();
  const { user } = useContext(AuthContext);
  const tenant = user?.attributes?.['custom:tenant_id'];

  const formData = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const [exportConfig, setExportConfig] = useState<
    AlertExportData | LogscaleExporter | null
  >(activeExporter as LogscaleExporter);
  const [shouldDisableSave, setShouldDisableSave] = useState(true);

  const { name, enabled, url, token, tls, index } = exportConfig || {};

  const fillForm = useCallback(() => {
    setValue('name', name || '');
    setValue('enabled', enabled || false);
    setValue('url', url || '');
    setValue('token', token || '');
    setValue('index', index || '');
    setValue('tls', tls || EXPORTER_DEFAULTS.tls);
  }, [setValue, name, enabled, url, token, index, tls]);

  useEffect(() => {
    setExportConfig(activeExporter as LogscaleExporter);
  }, [activeExporter]);

  useEffect(() => {
    fillForm();
  }, [activeExporter, exportConfig, fillForm]);

  const [saveExporter, { loading: saveLoading, error: _saveError }] =
    useSaveExporter({
      onCompleted: async (data) => {
        const { saveExporter: savedExporter } = data;
        const exporter_id = savedExporter?.exporter_id;

        // Refetch triggers cache eviction and data refresh in parent
        await refetchExporters();

        setShouldDisableSave(true);
        setIsUpdating(false);

        showToastMessage({
          status: 'success',
          title: saveSuccessTitle,
          description: saveSuccessDesc,
          isClosable: true,
          position: 'bottom-right',
        });
        clearInProgress();
        if (exporter_id) {
          navigate(exporter_id);
        }
      },
      onError: () => {
        setShouldDisableSave(false);
        setIsUpdating(false);
        showToastMessage({
          status: 'error',
          title: saveFailTitle,
          description: saveFailDesc,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const formIsPopulated = Object.values(formData).every(
    (value) => value !== '',
  );

  const formIsUnchanged = _.isEqual(formData, {
    name,
    enabled,
    url,
    token,
    index,
    tls,
  });

  const isSaveDisabled = formIsUnchanged || !formIsPopulated || saveLoading;

  // only need to do this once and update the state else where like after a save or delete network request.
  useEffect(() => {
    setShouldDisableSave(isSaveDisabled);
  }, [isSaveDisabled]);

  const submitAlertExport = async (data: AlertExportData) => {
    setShouldDisableSave(false);
    setIsUpdating(true);
    setExportConfig(data);
    const { name, enabled, url, token, index, tls } = data;
    const input = {
      tenant: tenant || '',
      name,
      enabled,
      exporter: ExporterType.Logscale,
      logscale: {
        url,
        token,
        index,
        tls: {
          verify_certificate: tls.verify_certificate,
        },
      },
      exporter_id: activeExporter?.exporter_id || null,
    };
    saveExporter({
      variables: {
        input,
      },
    });
  };

  useSetExporterFormState({
    formRef,
    isUpdating,
    saveLoading,
    deleteLoading,
    isSaveDisabled,
    formIsUnchanged,
    shouldDisableSave,
    setFormRef,
    setIsSaveLoading,
    setIsSaveDisabled,
    setIsFormUnchanged,
    setIsCancelDisabled,
  });

  return (
    <>
      <VStack my={2} spacing={4} alignItems="start">
        <Text textStyle="body-md">
          {humioInstructions}{' '}
          <Link
            isExternal
            color="blue.400"
            textDecoration="none"
            href={LOG_SCALE_DOCS}
          >
            {humioDocumentation}
          </Link>
          .
        </Text>
      </VStack>
      <VStack
        as="form"
        spacing={4}
        ref={formRef}
        align="flex-start"
        id="alertExportForm"
        onSubmit={handleSubmit(submitAlertExport)}
      >
        <Controller
          name="enabled"
          control={control}
          render={({
            field: { onChange, name, value },
          }: {
            field: ControllerRenderProps<AlertExportData, 'enabled'>;
          }) => (
            <Switch
              inline
              name={name}
              isChecked={value}
              onChange={onChange}
              data-testid="exportSwitch"
              isDisabled={isUpdating}
              label={formData.enabled ? enabledLabel : disabledLabel}
            />
          )}
        />
        <Flex w="100%">
          <Input
            {...register('name', { required: true })}
            isRequired
            label={forms.exporterName}
            isDisabled={isUpdating}
          />
        </Flex>
        <VStack w="100%" spacing={1} align="stretch">
          {errors.url && (
            <Text color="red.500" fontSize="sm">
              {errors.url.message || 'Invalid URL format'}
            </Text>
          )}
          <Input
            {...register('url', {
              required: true,
              validate: (value) => validateExporterUrl(value),
            })}
            isRequired
            label={forms.url}
            isDisabled={isUpdating}
          />
        </VStack>
        <Flex w="100%">
          <Input
            {...register('token', { required: true })}
            isRequired
            label={forms.token}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%">
          <Input
            {...register('index', { required: true })}
            isRequired
            label={forms.index}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%" alignItems="end" justifyContent="space-between">
          <Controller
            name="tls"
            control={control}
            render={({
              field: { onChange, name, value },
            }: {
              field: ControllerRenderProps<AlertExportData, 'tls'>;
            }) => (
              <ExporterSslSwitch
                name={name}
                value={value}
                isUpdating={isUpdating}
                onChange={(props: OnChangeSwitchProps) => {
                  onChange({
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                  setValue('tls', {
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                }}
              />
            )}
          />
          {!activeExporter?.inProgress && (
            <IconButton
              size="sm"
              variant="solid"
              colorScheme="red"
              aria-label={forms.deleteBtn}
              isDisabled={saveLoading || deleteLoading}
              data-testid="alert-exporter-delete-button"
              onClick={() => {
                setIsUpdating(true);
                onDeleteOpen();
              }}
              icon={
                deleteLoading ? (
                  <Spinner size={'xs'} />
                ) : (
                  <DeleteSharpIcon boxSize={5} />
                )
              }
            />
          )}
        </Flex>
      </VStack>
    </>
  );
};
